/**
 * The sign-in's photograph of a data centre's rack aisle, looking out through a glass wall onto the
 * standby set in a green field, at the resolution the screen can show.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A <picture> AND NOT A CSS BACKGROUND
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Two things a background image cannot do. It cannot be chosen by DEVICE pixels across
 * widths and densities at once — `image-set` picks by density alone, so a Retina laptop got
 * a file sized for a 1x screen and drew it at twice its size, and the solar photograph this
 * replaced read as soft (Hamzah, 2026-10-13: "not HD, not detailed"). And it cannot say
 * when it has arrived, so it cannot fade in. `srcset` with width descriptors and a `sizes`
 * that is the plate's real width lets the browser do the arithmetic: a 1512px Retina laptop
 * gets the 3036px file at its native size, a 1920px 1x monitor the 1920, a 3x phone the
 * 1965px portrait crop — the source's full height, at its own pixels, since the stacked layout
 * draws it at plain `cover`.
 *
 * `sizes` is the plate's width — the photograph covers the frame, so it is the larger of the
 * frame's width and its height times the photograph's aspect ratio (1.678 landscape, 0.7604
 * portrait). A browser that cannot evaluate `max()` there falls back to `100vw`, which only
 * ever picks a smaller file, never a wrong one.
 *
 * Until the photograph arrives, the plate shows a 32px copy of it inline (`signin.css`), so a
 * slow connection still gets the page's colours on the first frame; the photograph fades in
 * over it on load. The form never waits for any of this.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';

const WIDE = '/signin-site';
const TALL = '/signin-site-tall';
const STACKED = '(max-width: 939px)';
const SIZES_WIDE = 'max(100vw, 167.83vh)';
/* Stacked, the plate is drawn at plain `cover` (signin.css). */
const SIZES_TALL = 'max(100vw, 76.04vh)';

const wide = (ext: string): string =>
  `${WIDE}-1280.${ext} 1280w, ${WIDE}-1920.${ext} 1920w, ${WIDE}-3036.${ext} 3036w`;
const tall = (ext: string): string =>
  `${TALL}-828.${ext} 828w, ${TALL}-1280.${ext} 1280w, ${TALL}-1965.${ext} 1965w`;

export function SitePhoto(): ReactNode {
  const [loaded, setLoaded] = useState(false);
  const ref = useRef<HTMLImageElement | null>(null);

  /* A cached photograph can finish before React attaches `onLoad`; read it once on mount. */
  useEffect(() => {
    const img = ref.current;
    if (img && img.complete && img.naturalWidth > 0) setLoaded(true);
  }, []);

  return (
    <picture>
      <source media={STACKED} type="image/avif" srcSet={tall('avif')} sizes={SIZES_TALL} />
      <source media={STACKED} type="image/webp" srcSet={tall('webp')} sizes={SIZES_TALL} />
      <source type="image/avif" srcSet={wide('avif')} sizes={SIZES_WIDE} />
      <source type="image/webp" srcSet={wide('webp')} sizes={SIZES_WIDE} />
      <img
        ref={ref}
        className={loaded ? 'lm-si-photo-img lm-si-photo-in' : 'lm-si-photo-img'}
        src={`${WIDE}-1920.webp`}
        alt=""
        decoding="async"
        draggable={false}
        onLoad={() => setLoaded(true)}
      />
    </picture>
  );
}
